import { useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { PgsModelsCard } from "@/components/admin/pgs-models-card";
import { SiteFooter } from "@/components/site-footer";
import { SiteHeader } from "@/components/site-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  addAnnotationSourceOverride,
  deleteFeedback,
  listAdminSettings,
  listAnnotationConfig,
  listAnnotationSourceOverrides,
  listFeedback,
  listMembers,
  removeAnnotationSourceOverride,
  setAdminSetting,
  setAnnotationSourceEnabled,
} from "@/lib/admin.functions";
import { getProfile } from "@/lib/jobs.functions";
import { relativeTime } from "@/lib/format";

const ADMIN_SETTING_LABELS: Record<string, string> = {
  notify_admin_on_signup: "Email me when someone new signs up",
  admixture_enabled: "Enable ADMIXTURE ancestry-proportion estimate (mygenomic.art)",
  rag_chat_enabled: "Enable the genome.coach chat",
  wgs_confident_regions: "WGS: only trust a missing variant in well-mappable regions",
};

// What each setting does, shown under its switch. Keep in sync with the
// consumers named here (admin-panel documentation is a requirement).
const ADMIN_SETTING_HELP: Record<string, string> = {
  notify_admin_on_signup: "Sends an email to admins for every new account.",
  admixture_enabled:
    "Global switch for the ADMIXTURE step in the pipeline. A member's own opt-in is still required; off skips the step for everyone.",
  rag_chat_enabled: "Global switch for genome.coach's chat. Off disables it for every member.",
  wgs_confident_regions:
    "A WGS file normally lists only positions that differ from the reference, so a tracked trait site with no entry usually means \"two reference copies\". On: that is only assumed where reads map reliably (GIAB v3.3 regions outside low-mappability, segmental duplications and the HLA region, about 85% of the genome); elsewhere the site is reported as \"can't tell\". The same rule feeds the genome-wide polygenic scores. Off: a missing site always counts as reference (the old behaviour). Applies to reports processed after the change. Arrays and exomes are unaffected: a missing site there is always \"can't tell\".",
};

export const Route = createFileRoute("/_authenticated/admin")({
  head: () => ({
    meta: [
      { title: "Admin — Understand Your Genome" },
      {
        name: "description",
        content: "Manage annotation sources and see registered members.",
      },
    ],
  }),
  component: AdminPage,
});

function AdminPage() {
  const profileFn = useServerFn(getProfile);
  const membersFn = useServerFn(listMembers);
  const configFn = useServerFn(listAnnotationConfig);
  const setEnabledFn = useServerFn(setAnnotationSourceEnabled);
  const settingsFn = useServerFn(listAdminSettings);
  const setSettingFn = useServerFn(setAdminSetting);
  const feedbackFn = useServerFn(listFeedback);
  const deleteFeedbackFn = useServerFn(deleteFeedback);
  const overridesFn = useServerFn(listAnnotationSourceOverrides);
  const addOverrideFn = useServerFn(addAnnotationSourceOverride);
  const removeOverrideFn = useServerFn(removeAnnotationSourceOverride);
  const queryClient = useQueryClient();

  const [overrideSourceKey, setOverrideSourceKey] = useState("");
  const [overrideEmail, setOverrideEmail] = useState("");
  const [overrideMode, setOverrideMode] = useState<"allow" | "block">("allow");

  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ["profile"],
    queryFn: () => profileFn({}),
  });

  const isAdmin = profile?.role === "admin";

  const { data: members, isLoading: membersLoading } = useQuery({
    queryKey: ["admin-members"],
    queryFn: () => membersFn({}),
    enabled: isAdmin,
  });

  const { data: config, isLoading: configLoading } = useQuery({
    queryKey: ["annotation-config"],
    queryFn: () => configFn({}),
    enabled: isAdmin,
  });

  const { data: settings, isLoading: settingsLoading } = useQuery({
    queryKey: ["admin-settings"],
    queryFn: () => settingsFn({}),
    enabled: isAdmin,
  });

  const { data: feedback, isLoading: feedbackLoading } = useQuery({
    queryKey: ["admin-feedback"],
    queryFn: () => feedbackFn({}),
    enabled: isAdmin,
  });

  const { data: overrides, isLoading: overridesLoading } = useQuery({
    queryKey: ["annotation-source-overrides"],
    queryFn: () => overridesFn({}),
    enabled: isAdmin,
  });

  const toggleMutation = useMutation({
    mutationFn: (vars: { key: string; enabled: boolean }) => setEnabledFn({ data: vars }),
    onSuccess: (_data, vars) => {
      toast.success(`${vars.key} ${vars.enabled ? "enabled" : "disabled"}.`);
      queryClient.invalidateQueries({ queryKey: ["annotation-config"] });
    },
    onError: () => toast.error("Couldn't update that setting. Please try again."),
  });

  const settingMutation = useMutation({
    mutationFn: (vars: { key: string; enabled: boolean }) => setSettingFn({ data: vars }),
    onSuccess: () => {
      toast.success("Setting updated.");
      queryClient.invalidateQueries({ queryKey: ["admin-settings"] });
    },
    onError: () => toast.error("Couldn't update that setting. Please try again."),
  });

  const addOverrideMutation = useMutation({
    mutationFn: (vars: { sourceKey: string; email: string; mode: "allow" | "block" }) =>
      addOverrideFn({ data: vars }),
    onSuccess: () => {
      toast.success("Override added.");
      setOverrideEmail("");
      queryClient.invalidateQueries({ queryKey: ["annotation-source-overrides"] });
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Couldn't add that override."),
  });

  const removeOverrideMutation = useMutation({
    mutationFn: (id: string) => removeOverrideFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Override removed.");
      queryClient.invalidateQueries({ queryKey: ["annotation-source-overrides"] });
    },
    onError: () => toast.error("Couldn't remove that. Please try again."),
  });

  const deleteFeedbackMutation = useMutation({
    mutationFn: (id: string) => deleteFeedbackFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Feedback deleted.");
      queryClient.invalidateQueries({ queryKey: ["admin-feedback"] });
    },
    onError: () => toast.error("Couldn't delete that. Please try again."),
  });

  if (profileLoading) {
    return (
      <div className="flex min-h-screen flex-col">
        <SiteHeader />
        <main className="mx-auto w-full max-w-4xl flex-1 px-5 py-14">
          <p className="text-sm text-muted-foreground">Loading…</p>
        </main>
        <SiteFooter />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex min-h-screen flex-col">
        <SiteHeader />
        <main className="mx-auto w-full max-w-4xl flex-1 px-5 py-14">
          <div className="rounded-3xl border border-dashed border-border bg-card p-10 text-center">
            <ShieldCheck className="mx-auto size-8 text-muted-foreground" />
            <h1 className="mt-4 text-xl">You don't have access to this page</h1>
            <p className="mx-auto mt-2 max-w-sm text-sm text-muted-foreground">
              This area is for administrators only.
            </p>
            <Button asChild className="mt-6">
              <Link to="/dashboard">Back to my uploads</Link>
            </Button>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="mx-auto w-full max-w-4xl flex-1 px-5 py-14">
        <h1 className="text-3xl">Admin</h1>
        <p className="mt-2 max-w-xl leading-relaxed text-muted-foreground">
          Manage which annotation sources the pipeline uses, and see who's registered.
        </p>

        <Card className="mt-10 rounded-3xl">
          <CardHeader>
            <CardTitle>Annotation sources</CardTitle>
            <CardDescription>
              Toggles take effect for annotations started after the change.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {configLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
            <div className="divide-y divide-border">
              {config?.map((row) => (
                <label key={row.key} className="flex items-center justify-between gap-4 py-3">
                  <span className="font-mono text-sm">{row.key}</span>
                  <Switch
                    checked={row.enabled}
                    onCheckedChange={(checked) =>
                      toggleMutation.mutate({ key: row.key, enabled: checked })
                    }
                    disabled={toggleMutation.isPending}
                  />
                </label>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="mt-8 rounded-3xl">
          <CardHeader>
            <CardTitle>Per-user source access</CardTitle>
            <CardDescription>
              Allow or block one specific person for a source, on top of the global toggle above —
              e.g. a source that's off for everyone except one allow-listed account.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <div className="flex-1">
                <label className="text-xs text-muted-foreground">Source</label>
                <Select value={overrideSourceKey} onValueChange={setOverrideSourceKey}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Choose a source" />
                  </SelectTrigger>
                  <SelectContent>
                    {config?.map((row) => (
                      <SelectItem key={row.key} value={row.key}>
                        {row.key}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <label className="text-xs text-muted-foreground">User email</label>
                <Input
                  className="mt-1"
                  type="email"
                  placeholder="user@example.com"
                  value={overrideEmail}
                  onChange={(e) => setOverrideEmail(e.target.value)}
                />
              </div>
              <div className="w-full sm:w-36">
                <label className="text-xs text-muted-foreground">Mode</label>
                <Select
                  value={overrideMode}
                  onValueChange={(v) => setOverrideMode(v as "allow" | "block")}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="allow">Allow</SelectItem>
                    <SelectItem value="block">Block</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                disabled={addOverrideMutation.isPending || !overrideSourceKey || !overrideEmail}
                onClick={() =>
                  addOverrideMutation.mutate({
                    sourceKey: overrideSourceKey,
                    email: overrideEmail,
                    mode: overrideMode,
                  })
                }
              >
                Add
              </Button>
            </div>

            {overridesLoading && <p className="mt-4 text-sm text-muted-foreground">Loading…</p>}
            {overrides && overrides.length === 0 && (
              <p className="mt-4 text-sm text-muted-foreground">No per-user overrides yet.</p>
            )}
            {overrides && overrides.length > 0 && (
              <Table className="mt-4">
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Mode</TableHead>
                    <TableHead>Added</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {overrides.map((o) => (
                    <TableRow key={o.id}>
                      <TableCell className="break-all">{o.email ?? o.userId}</TableCell>
                      <TableCell className="font-mono text-sm">{o.sourceKey}</TableCell>
                      <TableCell className="capitalize">{o.mode}</TableCell>
                      <TableCell>{relativeTime(o.createdAt)}</TableCell>
                      <TableCell>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="text-muted-foreground hover:text-destructive"
                          disabled={removeOverrideMutation.isPending}
                          onClick={() => removeOverrideMutation.mutate(o.id)}
                          aria-label="Remove override"
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card className="mt-8 rounded-3xl">
          <CardHeader>
            <CardTitle>Settings</CardTitle>
            <CardDescription>Site-wide switches and admin alerts.</CardDescription>
          </CardHeader>
          <CardContent>
            {settingsLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
            <div className="divide-y divide-border">
              {settings?.map((row) => (
                <label key={row.key} className="flex items-center justify-between gap-4 py-3">
                  <span className="text-sm">
                    {ADMIN_SETTING_LABELS[row.key] ?? row.key}
                    {ADMIN_SETTING_HELP[row.key] && (
                      <span className="mt-1 block text-xs text-muted-foreground">
                        {ADMIN_SETTING_HELP[row.key]}
                      </span>
                    )}
                  </span>
                  <Switch
                    checked={row.enabled}
                    onCheckedChange={(checked) =>
                      settingMutation.mutate({ key: row.key, enabled: checked })
                    }
                    disabled={settingMutation.isPending}
                  />
                </label>
              ))}
            </div>
          </CardContent>
        </Card>

        <PgsModelsCard />

        <Card className="mt-8 rounded-3xl">
          <CardHeader>
            <CardTitle>Members</CardTitle>
            <CardDescription>Everyone who has signed up, read-only.</CardDescription>
          </CardHeader>
          <CardContent>
            {membersLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
            {members && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead className="text-right">Active jobs</TableHead>
                    <TableHead className="text-right">Total jobs</TableHead>
                    <TableHead>Last upload</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {members.map((m) => (
                    <TableRow key={m.userId}>
                      <TableCell className="break-all">{m.email ?? "—"}</TableCell>
                      <TableCell>{m.role}</TableCell>
                      <TableCell className="text-right">{m.activeJobCount}</TableCell>
                      <TableCell className="text-right">{m.totalJobCount}</TableCell>
                      <TableCell>
                        {m.lastUploadAt ? relativeTime(m.lastUploadAt) : "Never"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card className="mt-8 rounded-3xl">
          <CardHeader>
            <CardTitle>Feedback</CardTitle>
            <CardDescription>
              Submitted from the site footer. Admins get an email for each new one.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {feedbackLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
            {feedback && feedback.length === 0 && (
              <p className="text-sm text-muted-foreground">Nothing submitted yet.</p>
            )}
            {feedback && feedback.length > 0 && (
              <div className="divide-y divide-border">
                {feedback.map((f) => (
                  <div key={f.id} className="flex items-start justify-between gap-4 py-4">
                    <div className="min-w-0">
                      <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">
                        {f.message}
                      </p>
                      <p className="mt-2 text-xs text-muted-foreground">
                        {f.email ?? "Anonymous"} · {relativeTime(f.created_at)}
                      </p>
                    </div>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="shrink-0 text-muted-foreground hover:text-destructive"
                      disabled={deleteFeedbackMutation.isPending}
                      onClick={() => deleteFeedbackMutation.mutate(f.id)}
                      aria-label="Delete feedback"
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
      <SiteFooter />
    </div>
  );
}
